import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Menu, X, Phone } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import logo from '../../assets/logo.png';

const NAV = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  { label: 'Services', path: '/services' },
  { label: 'Contact', path: '/contact' },
];

// ── Scroll to top on every route change ──────────────────────────────────────
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

export default function PublicLayout() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col">
      <ScrollToTop />

      {/* Top bar */}
      <div className="bg-purple-700 text-white text-sm py-2 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <span className="flex items-center gap-2"><Phone size={13} /> +1 (317) 982-1959 — 24/7 Support</span>
          <span className="hidden sm:block">Serving Fort Wayne, Indianapolis and nearby counties</span>
        </div>
      </div>

      {/* Navbar */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src={logo} alt="MDS Transportation LLC" className="h-10 w-auto" />
            </Link>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-1">
              {NAV.map((item) => (
                <Link key={item.path} to={item.path}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${pathname === item.path ? 'text-purple-700 bg-purple-50' : 'text-gray-700 hover:text-purple-700 hover:bg-purple-50'}`}>
                  {item.label}
                </Link>
              ))}
            </div>

            <div className="hidden md:flex items-center gap-3">
              {user ? (
                <div className="flex items-center gap-3">
                  <button onClick={() => navigate(user.role === 'user' ? '/dashboard' : '/admin')}
                    className="text-sm font-medium text-purple-700 hover:underline">
                    Dashboard
                  </button>
                  <button onClick={logout} className="text-sm text-gray-500 hover:text-red-600">Logout</button>
                </div>
              ) : (
                <>
                  <Link to="/login" className="text-sm font-medium text-gray-700 hover:text-purple-700">Login</Link>
                  <Link to="/book" className="btn-primary text-sm py-2 px-5">Book a Ride</Link>
                </>
              )}
            </div>

            <button className="md:hidden p-2" onClick={() => setOpen(!open)}>
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="md:hidden border-t border-gray-100 bg-white px-4 py-3 space-y-1">
            {NAV.map((item) => (
              <Link key={item.path} to={item.path} onClick={() => setOpen(false)}
                className={`block px-4 py-2 rounded-lg text-sm font-medium ${pathname === item.path ? 'text-purple-700 bg-purple-50' : 'text-gray-700'}`}>
                {item.label}
              </Link>
            ))}
            <div className="pt-2 border-t border-gray-100 flex flex-col gap-2">
              {user ? (
                <>
                  <Link to={user.role === 'user' ? '/dashboard' : '/admin'} onClick={() => setOpen(false)} className="btn-outline text-sm text-center">Dashboard</Link>
                  <button onClick={() => { logout(); setOpen(false); }} className="text-sm text-red-500 py-2">Logout</button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} className="btn-outline text-sm text-center">Login</Link>
                  <Link to="/book" onClick={() => setOpen(false)} className="btn-primary text-sm text-center">Book a Ride</Link>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      <main className="flex-1"><Outlet /></main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <img src={logo} alt="MDS Transportation LLC" className="h-12 w-auto" />
              </div>
              <p className="text-gray-400 text-sm leading-relaxed max-w-sm">
                Providing safe, dependable, and accessible non-emergency medical transportation services.
                Serving Fort Wayne, Indianapolis and nearby counties.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-3 font-heading">Quick Links</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                {NAV.map(n => <li key={n.path}><Link to={n.path} className="hover:text-purple-400 transition-colors">{n.label}</Link></li>)}
                <li><Link to="/book" className="hover:text-purple-400 transition-colors">Book a Ride</Link></li>
                <li><Link to="/terms" className="hover:text-purple-400 transition-colors">Terms of Service</Link></li>
                <li><Link to="/privacy" className="hover:text-purple-400 transition-colors">Privacy Policy</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-3 font-heading">Contact</h4>
              <div className="space-y-2 text-sm text-gray-400">
                <p>📞 +1 (317) 982-1959</p>
                <p>📧 info@mdstransportation.com</p>
                <p>📍 5632 W Washington St<br />Indianapolis, IN 46241</p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-6 text-sm text-gray-500 flex flex-wrap items-center justify-between gap-3">
            <span>© {new Date().getFullYear()} MDS Transportation LLC. All rights reserved.</span>
            <div className="flex gap-4">
              <Link to="/terms" className="hover:text-purple-400 transition-colors">Terms of Service</Link>
              <Link to="/privacy" className="hover:text-purple-400 transition-colors">Privacy Policy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
